import { Capacitor } from "@capacitor/core";

// Height reserved for Android's system navigation bar. The 3-button bar is
// 48dp; the gesture bar is shorter, so on gesture devices this leaves a little
// extra (filled by the nav's own background) rather than covering the tabs.
const ANDROID_NAV_BAR_PX = 48;

/**
 * Android 15+ with targetSdk 35+ draws apps edge-to-edge: the WebView extends
 * under the system navigation bar. Newer WebViews report that through
 * env(safe-area-inset-bottom), older ones report 0 — and then the fixed bottom
 * tab bar ends up half hidden under the system bar.
 *
 * Native builds now set android.adjustMarginsForEdgeToEdge = "auto", which
 * keeps the WebView out from under the bars entirely. This covers the installs
 * built before that (the app loads its web code from the server, so they get
 * this fix without a store update): only when ALL of these hold do we reserve
 * the nav bar height via --android-nav-inset:
 *   - native Android, version 15 or newer;
 *   - the WebView fills the whole screen (it really is under the system bars —
 *     with margins applied it is shorter than the screen by the bars' height);
 *   - env(safe-area-inset-bottom) resolves to 0 (otherwise CSS already has it).
 */
export function applyAndroidNavInset(): void {
  try {
    if (Capacitor.getPlatform() !== "android") return;
    const match = navigator.userAgent.match(/Android (\d+)/);
    if (!match || Number(match[1]) < 15) return;

    const root = document.documentElement;
    if (root.style.getPropertyValue("--android-nav-inset")) return; // already decided

    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom, 0px)";
    document.body.appendChild(probe);
    const reportedInset = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    probe.remove();
    if (reportedInset > 0) return;

    // Measured on mount, when the keyboard is closed. A few px of slack for
    // rounding between screen and viewport sizes.
    const edgeToEdge = window.innerHeight >= window.screen.height - 8;
    if (!edgeToEdge) return;

    root.style.setProperty("--android-nav-inset", `${ANDROID_NAV_BAR_PX}px`);
  } catch {
    // Never let a layout nicety break the app shell.
  }
}
